/**
 * The share surface's access model — pure, no DOM, no network.
 *
 * SH-4. The merge of "Collaborators" and "Client review links" is a DATA
 * question before it is a DOM one: four row kinds from three endpoints, with
 * two permission models and two capability flags between them. Deciding all of
 * that inside a renderer is how the two dialogs drifted apart in the first
 * place.
 *
 * Three rules this module encodes, each of which has already cost something:
 *
 *   1. ORDERING IS A FACT, not a render detail. Owner, editors, pending
 *      invites, live links — and dead rows are a SEPARATE array that never
 *      interleaves. The fold is a view of `dead`, not a state a renderer keeps.
 *
 *   2. AN ABSENT CAPABILITY MEANS DISABLED. Every existing fixture predates
 *      SH-3a's `capabilities` field, so a reader that treated `undefined` as
 *      "on" would pass every test in this repo while production — where all
 *      three flags ship "false" — sat dark.
 *
 *   3. AN UNKNOWN FAILURE DOWNGRADES NOTHING. Only a typed `comments_disabled`
 *      or `user_not_found` may narrow the surface. A 500, a timeout or a parse
 *      error must leave the answer exactly where it was: silently removing an
 *      access type because the network hiccuped is worse than showing one that
 *      fails loudly when used.
 */

/** Milliseconds in a day, for every expiry answer this module gives. */
const DAY = 24 * 60 * 60 * 1000;

/**
 * The seat cap, and the reason it is counted the way it is.
 *
 * `members-handlers.js` enforces 5 in TWO places that count DIFFERENT things:
 * the pre-read counts members only, while the atomic insert counts members
 * PLUS live invites. The insert is the one that decides, so it is the one this
 * mirrors — count members alone and the UI will promise a seat the server then
 * refuses, with a message naming a number the user cannot see.
 */
export const MAX_SEATS = 5;

export function normalizeMember(member, { selfSub = '' } = {}) {
  const sub = String(member?.sub || '');
  return {
    kind: member?.role === 'owner' ? 'owner' : 'editor',
    id: sub,
    sub,
    name: member?.displayName || member?.email || sub || 'Unknown user',
    email: member?.email || '',
    role: member?.role === 'owner' ? 'owner' : 'editor',
    isSelf: Boolean(sub) && sub === String(selfSub),
    live: true
  };
}

export function normalizeInvite(invite, { now = Date.now() } = {}) {
  // `expired` is DERIVED by the server and may be absent on an older response;
  // the clock is the fallback, never the other way round.
  const expiresAt = Number(invite?.expiresAt) || 0;
  const expired = invite?.expired === true || (expiresAt > 0 && expiresAt <= now);
  return {
    kind: 'invite',
    id: String(invite?.id || ''),
    name: invite?.email || 'Pending invitation',
    email: invite?.email || '',
    delivery: invite?.delivery?.state || 'pending',
    deliveryReason: String(invite?.delivery?.reason || '').slice(0, 160),
    expiresAt,
    expiresInDays: expiresAt ? Math.max(0, Math.round((expiresAt - now) / DAY)) : null,
    live: !expired,
    state: expired ? 'expired' : 'pending'
  };
}

export function normalizeLink(link, { now = Date.now() } = {}) {
  const expiresAt = Number(link?.expiresAt) || 0;
  const revoked = link?.revokedAt != null;
  const expired = !revoked && expiresAt > 0 && expiresAt <= now;
  return {
    kind: 'link',
    id: String(link?.id || ''),
    // CE-6. Two link kinds, one lifecycle: 'comment' (rvw_) is every
    // pre-migration row and the default; 'content-edit' (ced_) opens the
    // client editor. Never converted between (share-access-contracts §2).
    access: link?.access === 'content-edit' ? 'content-edit' : 'comment',
    name: link?.reviewerLabel || 'Reviewer',
    createdAt: Number(link?.createdAt) || 0,
    expiresAt,
    expiresInDays: expiresAt ? Math.round((expiresAt - now) / DAY) : null,
    live: !revoked && !expired,
    state: revoked ? 'revoked' : expired ? 'expired' : 'active',
    // SH-9 fills this from the comments list before the row is normalized.
    // `null` means "not looked up", which is NOT the same as 0 and must not
    // render as "no comments yet".
    comments: typeof link?.comments === 'number' ? link.comments : null
  };
}

/**
 * The single vocabulary for an invitation's delivery state.
 *
 * One place, because the phrase appears in a row, in a menu and in an
 * announcement — and three copies is how "Invitation failed" and "Delivery
 * failed" end up on the same screen.
 */
export function describeDelivery(invite) {
  if (!invite?.live) return 'Invitation expired';
  const state =
    invite.delivery === 'failed'
      ? invite.deliveryReason
        ? `Email failed — ${invite.deliveryReason}`
        : 'Email failed'
      : invite.delivery === 'delivered'
        ? 'Invitation delivered'
        : 'Invitation pending';
  // THE EXPIRY IS THE HALF THAT DECIDES WHETHER TO RESEND, and no version of
  // this UI has ever shown it — an invitation dies on a clock and the row said
  // only whether the mail left the building. Omitted when the server did not
  // send one: an absent number is not the same as "expires today".
  if (invite.expiresInDays == null) return state;
  const when = invite.expiresInDays <= 0 ? 'Expires today' : `Expires in ${invite.expiresInDays}d`;
  return `${state} · ${when}`;
}

/** The single vocabulary for a link's state. Same reason as above. */
export function describeLinkState(link) {
  if (link?.state === 'revoked') return 'Revoked';
  if (link?.state === 'expired') return 'Expired';
  // SH-9. `comments === null` means NOT LOOKED UP and must stay silent; 0 is a
  // fact and says so. Collapsing the two would state something nobody
  // established, which is the "not opened yet" mistake in another costume.
  const said =
    link?.comments == null
      ? ''
      : link.comments === 0
        ? 'No comments yet'
        : `${link.comments} comment${link.comments > 1 ? 's' : ''}`;
  const noun = link?.access === 'content-edit' ? 'Can edit content' : 'Can comment';
  const when =
    link?.expiresInDays == null
      ? noun
      : link.expiresInDays <= 0
        ? `${noun} · Expires today`
        : `${noun} · Expires in ${link.expiresInDays}d`;
  return said ? `${said} · ${when}` : when;
}

/**
 * Seats left, counting what the SERVER's atomic insert counts.
 *
 * The owner is synthesised into the members list and is not a `project_members`
 * row, so they do not consume a seat.
 */
export function seatsRemaining({ members = [], invites = [], now = Date.now() } = {}) {
  const seatedMembers = members.filter((m) => m?.role !== 'owner').length;
  const liveInvites = invites.filter((i) => normalizeInvite(i, { now }).live).length;
  return Math.max(0, MAX_SEATS - seatedMembers - liveInvites);
}

/**
 * What this deployment will actually grant, and what the surface may claim.
 *
 * @param {Object} signals
 * @param {{reviewLinks?: boolean, invites?: boolean, reviewLinkDelivery?: boolean}} [signals.capabilitiesField]
 *   SH-3a's field. ABSENT means disabled — see rule 2 in the module docstring.
 * @param {{code?: string}|null} [signals.linksError]
 *   The error from the review-link list call, if it failed. A typed
 *   `comments_disabled` is the backstop probe; anything else is unknown.
 * @param {{code?: string}|null} [signals.lastAddError]
 *   The error from the most recent add, if any. `user_not_found` proves invites
 *   are off far more directly than any flag can.
 */
export function deriveCapabilities({
  capabilitiesField = null,
  linksError = null,
  lastAddError = null
} = {}) {
  // Rule 2: absent means disabled. `Boolean(undefined)` is already false; this
  // is written out because the default is the whole point of the field.
  let canComment = capabilitiesField?.reviewLinks === true;
  let canInvite = capabilitiesField?.invites === true;
  let canDeliverReviewLinks = canComment && capabilitiesField?.reviewLinkDelivery === true;
  // CE-6: the third compose mode. Advertised by members-handlers only when
  // CLIENT_EDIT_ENABLED and the owner's plan carries clientEdit — and enforced
  // again at the mint branch, so this field is a rendering fact, not a grant.
  let canClientEdit = capabilitiesField?.clientEditLinks === true;

  // Rule 3: only a TYPED refusal narrows the surface. An unknown failure — a
  // 500, a timeout, a parse error — leaves both answers exactly as they were.
  if (linksError?.code === 'comments_disabled') canComment = false;
  if (lastAddError?.code === 'user_not_found') canInvite = false;
  if (!canComment) canDeliverReviewLinks = false;

  return {
    canComment,
    canInvite,
    canDeliverReviewLinks,
    canClientEdit,
    // The sentence the compose block is allowed to say. SH-1/D2 deleted the
    // unconditional "we email a 72-hour invitation" because it was false in
    // production; this is where it earns its way back.
    inviteHint: canInvite
      ? 'If they do not have an account yet, we email a 72-hour invitation.'
      : 'People you add need a PageStitch account to open the project.'
  };
}

/**
 * One list from three endpoints.
 *
 * @returns {{live: Array, dead: Array, counts: {people: number, links: number, seats: number}}}
 */
export function mergeAccessRows({
  members = [],
  invites = [],
  links = [],
  selfSub = '',
  now = Date.now()
} = {}) {
  const people = members.map((m) => normalizeMember(m, { selfSub }));
  const owners = people.filter((p) => p.kind === 'owner');
  const editors = people.filter((p) => p.kind === 'editor');
  const inviteRows = invites.map((i) => normalizeInvite(i, { now }));
  const linkRows = links.map((l) => normalizeLink(l, { now }));

  // Rule 1: ordering is a fact. Owner, editors, pending invites, live links.
  const live = [
    ...owners,
    ...editors,
    ...inviteRows.filter((i) => i.live),
    ...linkRows.filter((l) => l.live)
  ];
  // Dead rows are history, not access. An expired invite and a revoked link
  // belong together and behind a fold, never interleaved with people who can
  // open the project right now.
  const dead = [...inviteRows.filter((i) => !i.live), ...linkRows.filter((l) => !l.live)];

  return {
    live,
    dead,
    counts: {
      people: owners.length + editors.length + inviteRows.filter((i) => i.live).length,
      links: linkRows.filter((l) => l.live).length,
      seats: seatsRemaining({ members, invites, now })
    }
  };
}

/**
 * Per-reviewer comment counts, bucketed from the comments list.
 *
 * SH-9. No server change: a guest comment carries `author_kind = 'guest'` and
 * `author_id = <review link id>`, surfaced as `authorKind` / `authorId`. So the
 * one fact that makes a link row worth reading — did the reviewer actually say
 * anything — is already on the wire.
 *
 * It is NOT "did they open it". That is unstorable without a schema change and
 * a D1 write on every guest page read, and stating it would be the same class
 * of claim as the "not opened yet" this initiative cut.
 *
 * MEMBER comments are excluded: they are not this reviewer's, and counting them
 * would credit a link for its owner's own notes.
 */
export function countCommentsByLink(threads = []) {
  const counts = new Map();
  for (const thread of threads) {
    for (const comment of thread?.comments || []) {
      if (comment?.authorKind !== 'guest') continue;
      const id = String(comment.authorId || '');
      if (!id) continue;
      counts.set(id, (counts.get(id) || 0) + 1);
    }
  }
  return counts;
}

/**
 * Which actions a viewer may perform on a row.
 *
 * Merging the two dialogs merges two permission models, and the combination is
 * not obvious: review links are owner-only to CREATE and REVOKE but ANY member
 * may LIST them, while member add is owner-only and remove is
 * owner-anyone/member-self.
 *
 * An action the viewer cannot perform is ABSENT, never disabled — a disabled
 * control reading "only the owner can revoke" is a permanent inline lecture on
 * every visit — and a row with no available action gets no trailing control at
 * all, because a trigger that opens an empty menu is worse than no trigger.
 */
export function actionsFor(row, { selfRole = null } = {}) {
  const isOwner = selfRole === 'owner';
  if (!row) return [];
  if (row.kind === 'owner') return [];
  if (row.kind === 'editor') {
    // An owner removes anyone; a member may remove only themselves, which the
    // surface renders as "Leave project" rather than as a row action.
    return isOwner ? ['remove'] : [];
  }
  if (row.kind === 'invite') return isOwner ? ['revoke-invite'] : [];
  if (row.kind === 'link') {
    if (!isOwner) return [];
    return row.live ? ['extend', 'revoke-link'] : ['recreate'];
  }
  return [];
}
